import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, tutorApi } from '../services/api';
import { isNonSGTBrowser, formatDateTimeSGTLabelled } from '../utils/dateUtils';
import EditLessonModal from './EditLessonModal';
import AdminAddLessonModal from './AdminAddLessonModal';
import '../assets/MonthlyReport.css';
import '../assets/PageHeader.css';
import '../assets/SharedTabs.css';

const MonthlyReport = ({ isTutorView = false }) => {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedMonths, setSelectedMonths] = useState(() => {
    const saved = localStorage.getItem('monthlyReport_selectedMonths');
    return saved ? JSON.parse(saved) : [new Date().getMonth() + 1];
  });
  const [selectedYears, setSelectedYears] = useState(() => {
    const saved = localStorage.getItem('monthlyReport_selectedYears');
    return saved ? JSON.parse(saved) : [new Date().getFullYear()];
  });
  const [selectedTutors, setSelectedTutors] = useState(() => {
    const saved = localStorage.getItem('monthlyReport_selectedTutors');
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedTutees, setSelectedTutees] = useState(() => {
    const saved = localStorage.getItem('monthlyReport_selectedTutees');
    return saved ? JSON.parse(saved) : [];
  });
  const [tutors, setTutors] = useState([]);
  const [tutees, setTutees] = useState([]);
  const [showTutorDropdown, setShowTutorDropdown] = useState(false);
  const [showTuteeDropdown, setShowTuteeDropdown] = useState(false);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem('monthlyReport_activeTab');
    return saved || 'lessons';
  });
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [editingLesson, setEditingLesson] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [commissionSubTab, setCommissionSubTab] = useState(() => {
    const saved = localStorage.getItem('monthlyReport_commissionSubTab');
    return saved || 'tutee';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddLessonModal, setShowAddLessonModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchLessons();
    fetchTutorsAndTutees();
  }, [selectedMonths, selectedYears]);

  useEffect(() => {
    localStorage.setItem('monthlyReport_selectedMonths', JSON.stringify(selectedMonths));
  }, [selectedMonths]);

  useEffect(() => {
    localStorage.setItem('monthlyReport_selectedYears', JSON.stringify(selectedYears));
  }, [selectedYears]);

  useEffect(() => {
    localStorage.setItem('monthlyReport_selectedTutors', JSON.stringify(selectedTutors));
  }, [selectedTutors]);

  useEffect(() => {
    localStorage.setItem('monthlyReport_selectedTutees', JSON.stringify(selectedTutees));
  }, [selectedTutees]);

  useEffect(() => {
    localStorage.setItem('monthlyReport_activeTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem('monthlyReport_commissionSubTab', commissionSubTab);
  }, [commissionSubTab]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.custom-multiselect')) {
        setShowTutorDropdown(false);
        setShowTuteeDropdown(false);
        setShowMonthDropdown(false);
        setShowYearDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTutorsAndTutees = async () => {
    try {
      const [tutorsRes, tuteesRes] = await Promise.all([
        adminApi.getTutors(),
        adminApi.getTutees()
      ]);
      setTutors(tutorsRes.data);
      setTutees(tuteesRes.data);
    } catch (err) {
      console.error('Failed to load tutors/tutees:', err);
    }
  };

  const fetchLessons = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (selectedMonths.length === 0 || selectedYears.length === 0) {
        setLessons([]);
        return;
      }
      
      const promises = [];
      selectedYears.forEach(year => {
        selectedMonths.forEach(month => {
          // Use tutorApi for tutor view, adminApi for admin view
          if (isTutorView) {
            promises.push(tutorApi.getLessons(month, year));
          } else {
            promises.push(adminApi.getMonthlyLessons(month, year));
          }
        });
      });
      
      const responses = await Promise.all(promises);
      const allLessons = responses.flatMap(response => response.data);
      setLessons(allLessons);
    } catch (err) {
      setError('Failed to load lessons');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleTutor = (tutorId) => {
    setSelectedTutors(prev => 
      prev.includes(tutorId) 
        ? prev.filter(id => id !== tutorId)
        : [...prev, tutorId]
    );
  };

  const toggleTutee = (tuteeId) => {
    setSelectedTutees(prev => 
      prev.includes(tuteeId) 
        ? prev.filter(id => id !== tuteeId)
        : [...prev, tuteeId]
    );
  };

  const toggleMonth = (month) => {
    setSelectedMonths(prev => 
      prev.includes(month) 
        ? prev.filter(m => m !== month)
        : [...prev, month].sort((a, b) => a - b)
    );
  };

  const toggleYear = (year) => {
    setSelectedYears(prev => 
      prev.includes(year) 
        ? prev.filter(y => y !== year)
        : [...prev, year].sort((a, b) => b - a)
    );
  };

  const filteredLessons = lessons.filter(lesson => {
    const matchesTutor = selectedTutors.length === 0 || selectedTutors.includes(lesson.tutor?._id);
    const matchesTutee = selectedTutees.length === 0 || selectedTutees.includes(lesson.tutee?._id);
    
    // Search filter
    const matchesSearch = !searchQuery || 
      lesson.tutor?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lesson.tutee?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lesson.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lesson.mode?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lesson.summary?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesTutor && matchesTutee && matchesSearch;
  });

  const totalRevenue = filteredLessons.reduce((sum, lesson) => {
    const durationHours = lesson.duration / 60;
    return sum + ((lesson.rate || 0) * durationHours);
  }, 0);
  
  const totalTutorPay = filteredLessons.reduce((sum, lesson) => {
    const durationHours = lesson.duration / 60;
    return sum + ((lesson.tutorRate || 0) * durationHours);
  }, 0);
  
  const totalCommission = totalRevenue - totalTutorPay;
  const totalLessons = filteredLessons.length;

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const currentYear = new Date().getFullYear();
  const startYear = 2019;
  const endYear = currentYear + 5;
  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => endYear - i);

  const formatDate = (dateString) => {
    return formatDateTimeSGTLabelled(dateString);
  };

  const handleGenerateInvoice = (recipientId, recipientType) => {
    if (selectedMonths.length === 1 && selectedYears.length === 1) {
      navigate('/admin/invoices', {
        state: {
          recipientId,
          recipientType,
          month: selectedMonths[0],
          year: selectedYears[0]
        }
      });
    } else {
      setError('Please select a single month and year to generate invoices');
      setTimeout(() => setError(''), 3000);
    }
  };

  const getMonthLabel = () => {
    if (selectedMonths.length === 0) return 'No Months';
    if (selectedMonths.length === 12) return 'All Months';
    if (selectedMonths.length === 1) return months.find(m => m.value === selectedMonths[0])?.label;
    return `${selectedMonths.length} months selected`;
  };

  const getYearLabel = () => {
    if (selectedYears.length === 0) return 'No Years';
    if (selectedYears.length === years.length) return 'All Years';
    if (selectedYears.length === 1) return selectedYears[0].toString();
    return `${selectedYears.length} years selected`;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedLessons = () => {
    const sorted = [...filteredLessons].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'createdAt':
        case 'date':
          aValue = new Date(a[sortField]);
          bValue = new Date(b[sortField]);
          break;
        case 'tutor':
          aValue = a.tutor?.name || '';
          bValue = b.tutor?.name || '';
          break;
        case 'tutee':
          aValue = a.tutee?.name || '';
          bValue = b.tutee?.name || '';
          break;
        case 'subject':
        case 'mode':
          aValue = a[sortField] || '';
          bValue = b[sortField] || '';
          break;
        case 'duration':
        case 'rate':
        case 'tutorRate':
          aValue = a[sortField] || 0;
          bValue = b[sortField] || 0;
          break;
        case 'totalCollected':
          aValue = (a.rate || 0) * (a.duration / 60);
          bValue = (b.rate || 0) * (b.duration / 60);
          break;
        case 'tutorPayment':
          aValue = (a.tutorRate || 0) * (a.duration / 60);
          bValue = (b.tutorRate || 0) * (b.duration / 60);
          break;
        case 'commission':
          aValue = ((a.rate || 0) - (a.tutorRate || 0)) * (a.duration / 60);
          bValue = ((b.rate || 0) - (b.tutorRate || 0)) * (b.duration / 60);
          break;
        default:
          aValue = a[sortField] || '';
          bValue = b[sortField] || '';
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
    
    return sorted;
  };

  const handleEditLesson = (lesson) => {
    setEditingLesson(lesson);
    setShowEditModal(true);
  };

  const handleSaveLesson = async (lessonId, updateData) => {
    try {
      await adminApi.updateLesson(lessonId, updateData);
      // Refresh lessons after successful update
      await fetchLessons();
      setError('');
    } catch (error) {
      console.error('Error updating lesson:', error);
      throw new Error(error.response?.data?.message || 'Failed to update lesson');
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (window.confirm('Are you sure you want to delete this lesson? This action cannot be undone.')) {
      try {
        setLoading(true);
        await adminApi.deleteLesson(lessonId);
        // Refresh lessons after successful delete
        await fetchLessons();
        setError('');
      } catch (error) {
        console.error('Error deleting lesson:', error);
        setError(error.response?.data?.message || 'Failed to delete lesson');
      } finally {
        setLoading(false);
      }
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const handleExportCSV = () => {
    const rows = getSortedLessons();
    if (rows.length === 0) return;

    const headers = [
      'Submitted', 'Lesson Date', 'Tutor', 'Tutee', 'Subject',
      'Duration (hrs)', 'Mode', 'Tutee Rate ($/hr)', 'Tutor Rate ($/hr)',
      'Total Collected ($)', 'Tutor Payment ($)', 'Commission ($)', 'Summary'
    ];

    const escape = (val) => {
      const str = val === null || val === undefined ? '' : String(val);
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"` : str;
    };

    const csvRows = rows.map(lesson => {
      const tuteeRate = lesson.rate || 0;
      const tutorRate = lesson.tutorRate || 0;
      const durationHours = lesson.duration / 60;
      const totalCollected = tuteeRate * durationHours;
      const tutorPayment = tutorRate * durationHours;
      const commission = totalCollected - tutorPayment;
      return [
        escape(formatDate(lesson.createdAt)),
        escape(formatDate(lesson.date)),
        escape(lesson.tutor?.name || ''),
        escape(lesson.tutee?.name || ''),
        escape(lesson.subject || ''),
        escape(durationHours.toFixed(2)),
        escape(lesson.mode || 'Online'),
        escape(tuteeRate),
        escape(tutorRate > 0 ? tutorRate : ''),
        escape(totalCollected.toFixed(2)),
        escape(tutorRate > 0 ? tutorPayment.toFixed(2) : ''),
        escape(tutorRate > 0 ? commission.toFixed(2) : ''),
        escape(lesson.summary || '')
      ].join(',');
    });

    const csv = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const monthLabel = getMonthLabel().replace(/\s/g, '-');
    const yearLabel = getYearLabel().replace(/\s/g, '-');
    link.href = url;
    link.download = `lessons-${monthLabel}-${yearLabel}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="monthly-report">
      <div className="page-header with-background">
        <div className="page-header-content">
          <div>
            <h1 className="page-header-title">Monthly Lessons Report</h1>
            <p className="page-header-subtitle">Comprehensive view of lessons, revenue, and commission analytics</p>
          </div>
          {!isTutorView && (
            <button 
              className="btn-add-lesson"
              onClick={() => setShowAddLessonModal(true)}
              title="Add new lesson"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
              Add Lesson
            </button>
          )}
        </div>
      </div>
      
      {successMessage && (
        <div className="alert alert-success" style={{ margin: '20px', marginBottom: '0' }}>
          {successMessage}
        </div>
      )}
      
      {/* Summary Cards */}
      <div className="report-summary-cards">
        <div className="summary-card">
          <div className="card-label">Total Lessons</div>
          <div className="card-value">{totalLessons}</div>
        </div>
        <div className="summary-card">
          <div className="card-label">Tutee Revenue</div>
          <div className="card-value">${totalRevenue.toFixed(2)}</div>
        </div>
        <div className="summary-card">
          <div className="card-label">Tutor Payments</div>
          <div className="card-value">${totalTutorPay.toFixed(2)}</div>
        </div>
        <div className="summary-card highlight">
          <div className="card-label">Total Commission</div>
          <div className="card-value">${totalCommission.toFixed(2)}</div>
          <div className="card-subtext">Per Co-Founder: ${(totalCommission / 2).toFixed(2)}</div>
        </div>
      </div>
      
      <div className="report-controls">
        {/* Month/Year Group */}
        <div className="filter-group-container">
          <div className="filter-group">
            <label>Month:</label>
            <div className="custom-multiselect">
              <div 
                className="multiselect-trigger"
                onClick={() => setShowMonthDropdown(!showMonthDropdown)}
              >
                <span>{getMonthLabel()}</span>
                <span className="dropdown-arrow">▼</span>
              </div>
              {showMonthDropdown && (
                <div className="multiselect-dropdown">
                  <div className="multiselect-option" onClick={() => {
                    if (selectedMonths.length === 12) {
                      setSelectedMonths([]);
                    } else {
                      setSelectedMonths(months.map(m => m.value));
                    }
                  }}>
                    <input 
                      type="checkbox" 
                      checked={selectedMonths.length === 12}
                      readOnly
                    />
                    <span>All Months</span>
                  </div>
                  {months.map(month => (
                    <div 
                      key={month.value} 
                      className="multiselect-option"
                      onClick={() => toggleMonth(month.value)}
                    >
                      <input 
                        type="checkbox" 
                        checked={selectedMonths.includes(month.value)}
                        readOnly
                      />
                      <span>{month.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="filter-group">
            <label>Year:</label>
            <div className="custom-multiselect">
              <div 
                className="multiselect-trigger"
                onClick={() => setShowYearDropdown(!showYearDropdown)}
              >
                <span>{getYearLabel()}</span>
                <span className="dropdown-arrow">▼</span>
              </div>
              {showYearDropdown && (
                <div className="multiselect-dropdown">
                  <div className="multiselect-option" onClick={() => {
                    if (selectedYears.length === years.length) {
                      setSelectedYears([]);
                    } else {
                      setSelectedYears(years);
                    }
                  }}>
                    <input 
                      type="checkbox" 
                      checked={selectedYears.length === years.length}
                      readOnly
                    />
                    <span>All Years</span>
                  </div>
                  {years.map(year => (
                    <div 
                      key={year} 
                      className="multiselect-option"
                      onClick={() => toggleYear(year)}
                    >
                      <input 
                        type="checkbox" 
                        checked={selectedYears.includes(year)}
                        readOnly
                      />
                      <span>{year}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="shared-tabs">
        <button 
          className={activeTab === 'lessons' ? 'active' : ''}
          onClick={() => setActiveTab('lessons')}
        >
          Lessons Data
        </button>
        <button 
          className={activeTab === 'commission' ? 'active' : ''}
          onClick={() => setActiveTab('commission')}
        >
          Commission Breakdown
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading lessons...</div>
      ) : (
        <>
          {activeTab === 'lessons' && (
            <div className="lessons-data-tab">
              {/* Search and Filters */}
              <div className="lessons-filters">
                <div className="filter-group">
                  <label>Search:</label>
                  <div className="search-input-wrapper">
                    <input
                      type="text"
                      placeholder="Search by tutor, tutee, subject..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="search-input"
                    />
                    {searchQuery && (
                      <button 
                        className="clear-search"
                        onClick={() => setSearchQuery('')}
                        title="Clear search"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>

                <div className="filter-group">
                  <label>Tutor:</label>
                  <div className="custom-multiselect">
                    <div 
                      className="multiselect-trigger"
                      onClick={() => setShowTutorDropdown(!showTutorDropdown)}
                    >
                      <span>
                        {selectedTutors.length === 0 
                          ? 'All Tutors' 
                          : `${selectedTutors.length} selected`}
                      </span>
                      <span className="dropdown-arrow">▼</span>
                    </div>
                    {showTutorDropdown && (
                      <div className="multiselect-dropdown">
                        <div className="multiselect-option" onClick={() => setSelectedTutors([])}>
                          <input 
                            type="checkbox" 
                            checked={selectedTutors.length === 0}
                            readOnly
                          />
                          <span>All Tutors</span>
                        </div>
                        {tutors.map(tutor => (
                          <div 
                            key={tutor._id} 
                            className="multiselect-option"
                            onClick={() => toggleTutor(tutor._id)}
                          >
                            <input 
                              type="checkbox" 
                              checked={selectedTutors.includes(tutor._id)}
                              readOnly
                            />
                            <span>{tutor.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="filter-group">
                  <label>Tutee:</label>
                  <div className="custom-multiselect">
                    <div 
                      className="multiselect-trigger"
                      onClick={() => setShowTuteeDropdown(!showTuteeDropdown)}
                    >
                      <span>
                        {selectedTutees.length === 0 
                          ? 'All Tutees' 
                          : `${selectedTutees.length} selected`}
                      </span>
                      <span className="dropdown-arrow">▼</span>
                    </div>
                    {showTuteeDropdown && (
                      <div className="multiselect-dropdown">
                        <div className="multiselect-option" onClick={() => setSelectedTutees([])}>
                          <input 
                            type="checkbox" 
                            checked={selectedTutees.length === 0}
                            readOnly
                          />
                          <span>All Tutees</span>
                        </div>
                        {tutees.map(tutee => (
                          <div 
                            key={tutee._id} 
                            className="multiselect-option"
                            onClick={() => toggleTutee(tutee._id)}
                          >
                            <input 
                              type="checkbox" 
                              checked={selectedTutees.includes(tutee._id)}
                              readOnly
                            />
                            <span>{tutee.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <button
                  className="btn-export-csv"
                  onClick={handleExportCSV}
                  disabled={filteredLessons.length === 0}
                  title={`Export ${filteredLessons.length} lessons to CSV`}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Export CSV ({filteredLessons.length})
                </button>
              </div>

              <div className="lessons-table-container">
                <table className="lessons-table detailed">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('createdAt')} className={`sortable${isNonSGTBrowser() ? ' sgt-tz-col-header' : ''}`}>
                        Submitted {getSortIcon('createdAt')}
                      </th>
                      <th onClick={() => handleSort('date')} className={`sortable${isNonSGTBrowser() ? ' sgt-tz-col-header' : ''}`}>
                        Lesson Date {getSortIcon('date')}
                      </th>
                      <th onClick={() => handleSort('tutor')} className="sortable">
                        Tutor {getSortIcon('tutor')}
                      </th>
                      <th onClick={() => handleSort('tutee')} className="sortable">
                        Tutee {getSortIcon('tutee')}
                      </th>
                      <th onClick={() => handleSort('subject')} className="sortable">
                        Subject {getSortIcon('subject')}
                      </th>
                      <th onClick={() => handleSort('duration')} className="sortable">
                        Duration {getSortIcon('duration')}
                      </th>
                      <th onClick={() => handleSort('mode')} className="sortable">
                        Mode {getSortIcon('mode')}
                      </th>
                      <th onClick={() => handleSort('rate')} className="sortable">
                        Tutee Rate {getSortIcon('rate')}
                      </th>
                      <th onClick={() => handleSort('tutorRate')} className="sortable">
                        Tutor Rate {getSortIcon('tutorRate')}
                      </th>
                      <th onClick={() => handleSort('totalCollected')} className="sortable">
                        Total Collected {getSortIcon('totalCollected')}
                      </th>
                      <th onClick={() => handleSort('tutorPayment')} className="sortable">
                        Tutor Payment {getSortIcon('tutorPayment')}
                      </th>
                      <th onClick={() => handleSort('commission')} className="sortable">
                        Commission {getSortIcon('commission')}
                      </th>
                      <th>Summary</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLessons.length === 0 ? (
                      <tr>
                        <td colSpan="14" className="no-data">No lessons found for this period</td>
                      </tr>
                    ) : (
                      <>
                        {getSortedLessons().map(lesson => {
                          const tuteeRate = lesson.rate || 0;
                          const tutorRate = lesson.tutorRate || 0;
                          const durationHours = lesson.duration / 60;
                          const totalCollected = tuteeRate * durationHours;
                          const tutorPayment = tutorRate * durationHours;
                          const commission = totalCollected - tutorPayment;
                          
                          return (
                            <tr key={lesson._id}>
                              <td className="date-cell">{formatDate(lesson.createdAt)}</td>
                              <td className="date-cell">{formatDate(lesson.date)}</td>
                              <td>{lesson.tutor?.name || 'N/A'}</td>
                              <td>{lesson.tutee?.name || 'N/A'}</td>
                              <td>{lesson.subject}</td>
                              <td>{(lesson.duration / 60).toFixed(2)} hrs</td>
                              <td>{lesson.mode || 'Online'}</td>
                              <td className="rate-cell">${tuteeRate}/hr</td>
                              <td className="rate-cell">
                                {tutorRate > 0 ? `$${tutorRate}/hr` : <span className="pending">Pending</span>}
                              </td>
                              <td className="total-cell">${totalCollected.toFixed(2)}</td>
                              <td className="payment-cell">
                                {tutorRate > 0 ? `$${tutorPayment.toFixed(2)}` : <span className="pending">-</span>}
                              </td>
                              <td className="commission-cell">
                                {tutorRate > 0 ? `$${commission.toFixed(2)}` : <span className="pending">-</span>}
                              </td>
                              <td className="summary-cell" title={lesson.summary}>
                                {lesson.summary ? (
                                  lesson.summary.length > 50 
                                    ? `${lesson.summary.substring(0, 50)}...`
                                    : lesson.summary
                                ) : '-'}
                              </td>
                              <td className="actions-cell">
                                <button 
                                  className="action-btn edit-btn"
                                  onClick={() => handleEditLesson(lesson)}
                                  title="Edit lesson"
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                    <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                  </svg>
                                </button>
                                <button 
                                  className="action-btn delete-btn"
                                  onClick={() => handleDeleteLesson(lesson._id)}
                                  title="Delete lesson"
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3,6 5,6 21,6"/>
                                    <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
                                    <line x1="10" y1="11" x2="10" y2="17"/>
                                    <line x1="14" y1="11" x2="14" y2="17"/>
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'commission' && (
            <div className="commission-tab">
              <div className="commission-analytics">
                <h2>Commission Breakdown</h2>
                
                {/* Sub-tab Navigation */}
                <div className="sub-tab-navigation">
                  <button 
                    className={`sub-tab-button ${commissionSubTab === 'tutee' ? 'active' : ''}`}
                    onClick={() => setCommissionSubTab('tutee')}
                  >
                    By Tutee
                  </button>
                  <button 
                    className={`sub-tab-button ${commissionSubTab === 'tutor' ? 'active' : ''}`}
                    onClick={() => setCommissionSubTab('tutor')}
                  >
                    By Tutor
                  </button>
                </div>

                {commissionSubTab === 'tutee' && (
                  <div className="analytics-section">
                    <div className="analytics-table-container">
                      <table className="analytics-table">
                        <thead>
                          <tr>
                            <th>Tutee</th>
                            <th>Lessons</th>
                            <th>Revenue</th>
                            <th>Tutor Pay</th>
                            <th>Commission</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const tuteeStats = {};
                            filteredLessons.forEach(lesson => {
                              const tuteeId = lesson.tutee?._id;
                              const tuteeName = lesson.tutee?.name || 'Unknown';
                              if (!tuteeStats[tuteeId]) {
                                tuteeStats[tuteeId] = {
                                  id: tuteeId,
                                  name: tuteeName,
                                  lessons: 0,
                                  revenue: 0,
                                  tutorPay: 0,
                                  commission: 0
                                };
                              }
                              const durationHours = lesson.duration / 60;
                              tuteeStats[tuteeId].lessons++;
                              tuteeStats[tuteeId].revenue += (lesson.rate || 0) * durationHours;
                              tuteeStats[tuteeId].tutorPay += (lesson.tutorRate || 0) * durationHours;
                              tuteeStats[tuteeId].commission += ((lesson.rate || 0) - (lesson.tutorRate || 0)) * durationHours;
                            });
                            
                            return Object.values(tuteeStats)
                              .sort((a, b) => b.commission - a.commission)
                              .map((stat, idx) => (
                                <tr key={idx}>
                                  <td className="name-cell">{stat.name}</td>
                                  <td className="center-cell">{stat.lessons}</td>
                                  <td className="amount-cell">${stat.revenue.toFixed(2)}</td>
                                  <td className="amount-cell">${stat.tutorPay.toFixed(2)}</td>
                                  <td className="commission-amount">${stat.commission.toFixed(2)}</td>
                                  <td className="action-cell">
                                    <button
                                      className="invoice-btn"
                                      onClick={() => handleGenerateInvoice(stat.id, 'tutee')}
                                      title="Generate invoice for this tutee"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                        <polyline points="14,2 14,8 20,8"/>
                                        <line x1="16" y1="13" x2="8" y2="13"/>
                                        <line x1="16" y1="17" x2="8" y2="17"/>
                                        <polyline points="10,9 9,9 8,9"/>
                                      </svg>
                                      Invoice
                                    </button>
                                  </td>
                                </tr>
                              ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {commissionSubTab === 'tutor' && (
                  <div className="analytics-section">
                    <div className="analytics-table-container">
                      <table className="analytics-table">
                        <thead>
                          <tr>
                            <th>Tutor</th>
                            <th>Lessons</th>
                            <th>Revenue</th>
                            <th>Tutor Pay</th>
                            <th>Commission</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const tutorStats = {};
                            filteredLessons.forEach(lesson => {
                              const tutorId = lesson.tutor?._id;
                              const tutorName = lesson.tutor?.name || 'Unknown';
                              if (!tutorStats[tutorId]) {
                                tutorStats[tutorId] = {
                                  id: tutorId,
                                  name: tutorName,
                                  lessons: 0,
                                  revenue: 0,
                                  tutorPay: 0,
                                  commission: 0
                                };
                              }
                              const durationHours = lesson.duration / 60;
                              tutorStats[tutorId].lessons++;
                              tutorStats[tutorId].revenue += (lesson.rate || 0) * durationHours;
                              tutorStats[tutorId].tutorPay += (lesson.tutorRate || 0) * durationHours;
                              tutorStats[tutorId].commission += ((lesson.rate || 0) - (lesson.tutorRate || 0)) * durationHours;
                            });
                            
                            return Object.values(tutorStats)
                              .sort((a, b) => b.commission - a.commission)
                              .map((stat, idx) => (
                                <tr key={idx}>
                                  <td className="name-cell">{stat.name}</td>
                                  <td className="center-cell">{stat.lessons}</td>
                                  <td className="amount-cell">${stat.revenue.toFixed(2)}</td>
                                  <td className="amount-cell">${stat.tutorPay.toFixed(2)}</td>
                                  <td className="commission-amount">${stat.commission.toFixed(2)}</td>
                                  <td className="action-cell">
                                    <button
                                      className="invoice-btn"
                                      onClick={() => handleGenerateInvoice(stat.id, 'tutor')}
                                      title="Generate invoice for this tutor"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                        <polyline points="14,2 14,8 20,8"/>
                                        <line x1="16" y1="13" x2="8" y2="13"/>
                                        <line x1="16" y1="17" x2="8" y2="17"/>
                                        <polyline points="10,9 9,9 8,9"/>
                                      </svg>
                                      Invoice
                                    </button>
                                  </td>
                                </tr>
                              ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
      
      <EditLessonModal
        lesson={editingLesson}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingLesson(null);
        }}
        onSave={handleSaveLesson}
      />
      
      {!isTutorView && (
        <AdminAddLessonModal
          isOpen={showAddLessonModal}
          onClose={() => setShowAddLessonModal(false)}
          onSuccess={(message) => {
            setSuccessMessage(message);
            fetchLessons();
            setTimeout(() => setSuccessMessage(''), 5000);
          }}
        />
      )}
    </div>
  );
};

export default MonthlyReport;